import { useMutation } from '@tanstack/react-query';
import axios, { AxiosError } from 'axios';
import { toast } from 'sonner';
import { config } from '../config';

export type ReportKind = 'bug' | 'feature' | 'other';

export interface SubmitUserReportParams {
  kind: ReportKind;
  description: string;
  /** Route the user was on when they opened the widget. */
  pageUrl: string;
  appVersion?: string;
}

interface SubmitUserReportResponse {
  id: string;
}

interface ApiErrorResponse {
  error: string;
  description?: string;
}

/**
 * Submits an in-app "Report an issue" entry (bug / feature request). The
 * backend attaches the reporter and organization from the session, so only the
 * report content and page context travel in the body. Fire-and-forget: nothing
 * cached client-side to invalidate.
 */
export function useSubmitUserReport() {
  return useMutation<SubmitUserReportResponse, AxiosError<ApiErrorResponse>, SubmitUserReportParams>({
    mutationFn: async (params: SubmitUserReportParams) => {
      const response = await axios.post<SubmitUserReportResponse>(
        `${config.backendApi}/api/user-reports`,
        params,
        { withCredentials: true },
      );
      return response.data;
    },
    onSuccess: () => {
      toast.success('Thanks — your report has been sent.');
    },
    onError: (err) => {
      toast.error(
        err.response?.data.description ??
          err.response?.data.error ??
          'Failed to send report',
      );
    },
  });
}
